import React, { useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import Button from "../Button";
import SimpleModal from "./SimpleModal";
import mfa from "../../services/mfa";
import { hideModal } from "../../store/actions/modal";
import { Title, SubLabel, Label, Input } from "./styles";

interface Props {
  isOpen: boolean;
  toggle: () => void;
  handleNextModal: () => void;
  isVerified: () => void;
}

const validationSchema = Yup.object().shape({
  token: Yup.string()
    .required("Este campo es requerido")
    .matches(/^[0-9]+$/, "Debe contener solo números")
    .length(6, "Debe tener 6 dígitos"),
});

const VerifyMfaCodeModal: React.FC<Props> = ({
  isOpen,
  toggle,
  handleNextModal,
  isVerified,
}) => {
  const dispatch = useDispatch();
  const tokenInputRef = useRef<HTMLInputElement>(null);

  // Autofocus the token input when modal opens (desktop only)
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    if (isOpen && !isMobile) {
      const focusInput = () => {
        if (tokenInputRef.current) {
          tokenInputRef.current.focus();
        } else {
          // Fallback: find input by name
          const input = document.querySelector('input[name="token"]') as HTMLInputElement;
          if (input) {
            input.focus();
          }
        }
      };
      
      // Try multiple times to ensure modal animation is complete
      setTimeout(focusInput, 100);
      setTimeout(focusInput, 300);
    }
  }, [isOpen]);

  const handleClose = () => {
    dispatch(hideModal());
    setTimeout(() => {
      toggle();
    }, 50);
  };

  const handleSubmit = async (
    values: { token: string },
    { setSubmitting, setFieldError }: any
  ) => {
    try {
      const response = await mfa.verifyAuthWithToken({
        token: values.token.replace(/\s/g, ""),
      });

      if (response.success) {
        toast.success("Código de autenticación verificado");
        isVerified();
        handleNextModal();
        toggle();
      } else {
        setFieldError("token", "Código de verificación incorrecto");
        toast.error("Código de verificación incorrecto");
      }
    } catch (error: any) {
      setFieldError("token", "Error al verificar el código");
      toast.error(error.message || "Error al verificar el código");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SimpleModal isOpen={isOpen} onClose={toggle} title="" size="sm">
      <div style={{ padding: "24px" }} className="modal-content-responsive">
        <Title style={{ fontSize: "18px" }}>Verificar identidad</Title>
        <SubLabel>
          Introduce el código de tu aplicación de autenticación para continuar.
        </SubLabel>

        <Formik
          initialValues={{ token: "" }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnBlur={false}
          validateOnChange={false}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            isSubmitting,
            resetForm,
          }) => (
            <Form>
              <div style={{ marginTop: "16px" }}>
                <Label>Código de autenticación</Label>
                <Input
                  type="text"
                  name="token"
                  placeholder="Ingresa el código de 6 dígitos"
                  value={values.token}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    handleChange({ target: { name: 'token', value } });
                  }}
                  disabled={isSubmitting}
                  className={errors.token && touched.token ? "error-field" : ""}
                  maxLength={6}
                  ref={tokenInputRef}
                />
                {errors.token && touched.token && (
                  <div
                    style={{
                      color: "#ef4444",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {errors.token}
                  </div>
                )}
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  gap: "12px",
                  marginTop: "24px",
                }}
                className="modal-footer-responsive"
              >
                <Button
                  type="button"
                  options={{ skin: "secondary", size: "md", type: "filled" }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    resetForm();
                    handleClose();
                  }}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  options={{ skin: "primary", size: "md", type: "filled" }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Verificando..." : "Verificar"}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .modal-content-responsive {
            padding: 16px !important;
          }
          .modal-footer-responsive {
            flex-direction: column !important;
          }
          .modal-footer-responsive button {
            width: 100%;
          }
        }
      `}</style>
    </SimpleModal>
  );
};

export default VerifyMfaCodeModal;
